<script setup>
import GoogleIcon from '@iconify-vue/logos/google-icon';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useTabs } from '@js/composables/tabs';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { googleIsConnected, facebookConfiguration, googleAuthUrl } = defineProps(
    {
        googleAuthUrl: {
            required: true,
            type: String,
        },
        googleIsConnected: {
            required: true,
            type: Boolean,
        },
        facebookConfiguration: {
            required: true,
            type: Object,
        },
    },
);

const { route } = useRoute();
const { visitPreservingTabs } = useTabs();

const form = reactive({
    facebookAccessToken: facebookConfiguration.data.accessToken,
});

const isSubmitting = ref(false);

const selectedTab = ref('google');

const validationSchema = object({
    facebookAccessToken: string().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(route('front.postSettings'), {
            facebookAccessToken: form.facebookAccessToken,
        })
        .then(() => {
            toastSaved();

            visitPreservingTabs(
                route('front.settings', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                google: {
                    title: 'Google',
                    contentSlotName: 'google',
                    forceMount: true,
                },
                facebook: {
                    title: 'Facebook',
                    contentSlotName: 'facebook',
                    forceMount: true,
                },
            }"
        >
            <template #google>
                <FormColumn>
                    <FormContent>
                        <div class="flex items-center gap-2">
                            <a
                                :href="googleAuthUrl"
                                class="inline-flex items-center justify-start rounded-xs bg-[#4285F4] p-0.5 text-white duration-200 hover:bg-[#3367d6]"
                            >
                                <span class="rounded-xs bg-white p-2">
                                    <GoogleIcon class="size-6" />
                                </span>

                                <span
                                    class="overflow-hidden p-2 text-ellipsis whitespace-nowrap"
                                >
                                    Se connecter avec Google
                                </span>
                            </a>

                            <div
                                v-if="googleIsConnected"
                                class="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                            >
                                <span
                                    class="size-2 rounded-full bg-green-500"
                                ></span>

                                Connecté
                            </div>

                            <div
                                v-else
                                class="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
                            >
                                <span
                                    class="size-2 rounded-full bg-red-500"
                                ></span>

                                Non connecté
                            </div>
                        </div>
                    </FormContent>
                </FormColumn>
            </template>

            <template #facebook>
                <FormColumn>
                    <FormContent>
                        <InputField
                            v-model="form.facebookAccessToken"
                            label="Jeton d'accès"
                            field-name="facebookAccessToken"
                            field-id="facebookAccessToken"
                            required
                        />
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter
            v-if="selectedTab === 'facebook'"
            :is-submitting="isSubmitting"
            :errors="errors"
        />
    </FormRoot>
</template>
